import * as React from 'react';
import getConversationNodeCount from 'mini-mail-list-store/lib/utils/getConversationNodeCount';
import getMailActionCustomData from 'mini-mail-list-store/lib/utils/getMailActionCustomData';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import hideToast, { TOAST_DIV_ID } from 'mini-toast/lib/utils/hideToast';
import isItemCached from 'mini-mail-prefetch/lib/actions/helper/isItemCached';
import MailSwipeAction from 'owa-service/lib/contract/MailSwipeAction';
import multiSelect from 'mini-mail-list-store/lib/actions/multiSelect/multiSelect';
import safePerformanceNow from 'mini-performance/lib/utils/safePerformanceNow';
import strings from 'owa-strings/owa-mini-strings';
import { addAriaLiveLabel, removeAriaLiveLabel } from 'mini-accessibility';
import { getListViewStore } from 'mini-mail-list-store/lib/store/Store';
import { getRTL } from '@uifabric/utilities/lib/rtl';
import { getSwipeAction } from '../../utils/swipeActions/getSwipeAction';
import { logUsage, PerformanceDatapoint } from 'owa-analytics';
import { MailListItemDataProps } from '../../schema/MailListItemDataProps.types';
import { RichContents } from 'mini-mail-list-item/lib/schema/RichContents';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { singleSelectMailItem } from 'mini-mail-list-actions';
import {
    getMailSwipeActionName,
    getSwipeActionBackground,
    getSwipeActionName,
    getSwipeActionText,
} from '../../utils/swipeActions/swipeActionProperties';
import touchBehavior, {
    TouchState,
    TouchThreshold,
    SwipeStage,
} from 'touch-behavior/lib/components/TouchBehavior';
import {
    MAIL_READING_PANE_LOAD_START,
    SWIPE_ACTION_BEGIN,
} from 'mini-mail-workflow-tracking/lib/constants/ListViewWorkflowConstants';

const styles = require('./MailListItemTouchBehavior.scss');
const classNames = require('classnames/bind').bind(styles);

export interface MailListItemViewProps {
    mailListItemDataProps: MailListItemDataProps;
    tableViewId: string;
    richContents?: RichContents;
    onMailListItemClick?: (rowId: string) => void;
    onMailListItemSwipe?: (rowId: string, operationName: MailSwipeAction) => void;
}

type SupportedItemCardSwipeActionClassNames =
    | 'mailListItemCard-swipeAction'
    | 'mailListItemCard-withoutRsvp-swipeAction'
    | 'mailListItemCard-withoutPreview-swipeAction'
    | 'mailListItemCard-withoutPreview-withoutRsvp-swipeAction';

export function mailListItemTouchBehavior<T extends MailListItemViewProps>(
    MailListItemComponent: React.ComponentClass<T>
): React.ComponentClass<T> {
    const threshold: TouchThreshold = {
        swipe: 3,
        action: 100,
        edgeSwipe: 15,
        longPress: 590,
    };

    return touchBehavior<T>({
        Component: MailListItemComponent,
        originalLeftSwipeRenderer: swipeRenderer(true /*isLeftSwipe*/),
        originalRightSwipeRenderer: swipeRenderer(false /*isLeftSwipe*/),
        originalGetLeftSwipeAction: getListItemSwipeAction(true /*isLeftSwipe*/),
        originalGetRightSwipeAction: getListItemSwipeAction(false /*isLeftSwipe*/),
        clickAction: clickAction,
        threshold: threshold,
        skipHandleTouchCallback: isListViewMultiSelecteEnabled,
        longPressAction: longPressAction,
        notifyOnSwipe: swipePerfMeasurer,
        shouldHandleRTLScenario: false,
    });
}

const swipePerfMeasurer = (() => {
    let perfDataPoint = null;

    function startDatapoint() {
        // invalidate already running datapoint
        invalidateDatapoint();
        perfDataPoint = new PerformanceDatapoint('MailListItemSwipePerf');
    }

    function endDatapoint() {
        perfDataPoint && perfDataPoint.end();
    }

    function invalidateDatapoint() {
        perfDataPoint && perfDataPoint.invalidate();
    }

    return (props: MailListItemViewProps, stage: SwipeStage) => {
        switch (stage) {
            case SwipeStage.Started:
                startDatapoint();
                break;
            case SwipeStage.Cancelled:
                invalidateDatapoint();
                break;
            case SwipeStage.Completed:
                endDatapoint();
                break;
            default:
                break;
        }
    };
})();

function clickAction(props: MailListItemViewProps) {
    const {
        mailListItemDataProps: { rowId, hasAttachment, unreadCount },
        onMailListItemClick,
        tableViewId,
    } = props;

    if (isListViewMultiSelecteEnabled()) {
        multiSelect(rowId, 'MailListItem' /* source */);
    } else {
        const tableView = getListViewStore().tableViews.get(tableViewId);
        const listViewType = tableView.tableQuery.listViewType;

        signalWorkflowEvent(MAIL_READING_PANE_LOAD_START, {
            itemId: rowId,
            isUIAction: true,
            ...getMailActionCustomData(),
            att: hasAttachment,
            uc: unreadCount,
            nc: getConversationNodeCount(rowId, tableView.id, listViewType),
            ic: isItemCached(rowId, tableView),

            // Mail Reading Pane Load start marker
            st: Math.round(safePerformanceNow()),
        });

        singleSelectMailItem(rowId);

        onMailListItemClick?.(rowId);
    }
}

const getListItemSwipeAction = (isLeftSwipe: boolean) => () => {
    const swipeActionFromConfig = getSwipeActionName(isLeftSwipe);
    const swipeAction = getSwipeAction(swipeActionFromConfig);
    const swipeActionName = getMailSwipeActionName(swipeActionFromConfig);
    if (swipeAction) {
        return (props: MailListItemViewProps) => {
            // Hide the previous toast message if there is any in case of confirmation dialog
            // Bug 90636: Toast massage is overlapping while performing the quick actions(delete and archive) at same time in Delete Folder.
            if (document.getElementById(TOAST_DIV_ID)) {
                // if it is already open, close the older sheet to reopen a new sheet to fix
                hideToast();
            }
            const { onMailListItemSwipe, mailListItemDataProps } = props;
            onMailListItemSwipe?.(mailListItemDataProps.rowId, swipeActionFromConfig);
            logSwipeAction(isLeftSwipe, swipeActionName);
            swipeAction(props);
        };
    } else {
        return null;
    }
};

const swipeRenderer = (isLeftSwipe: boolean) => (
    props: MailListItemViewProps,
    state: TouchState,
    threshold: TouchThreshold
) => {
    /**
     * WI #33049: While using an RTL language, left directions remains the same, but we need to ensure
     * that the right action's HTML is rendered in the correct order. Hence we are swapping the
     * action to render in the correct order.
     */
    const swipeActionType = getRTL() ? !isLeftSwipe : isLeftSwipe;
    const swipeActionName = getSwipeActionName(swipeActionType);
    if (swipeActionName === MailSwipeAction.None) {
        return null;
    }

    const backgroundColorEnabled = swipeActionType
        ? state.moveX > threshold.action || state.leftAction
        : state.moveX < -threshold.action || state.rightAction;
    const backgroundColor = getSwipeActionBackground(swipeActionName);
    const { ShowPreviewTextInListView } = getUserConfiguration().UserOptions;

    const swipeActionStyles = classNames(
        styles['mailListItem-swipeAction'],
        styles[isLeftSwipe ? 'mailListItem-swipeAction--left' : 'mailListItem-swipeAction--right'],
        { [backgroundColor]: backgroundColorEnabled },
        getMailListItemCardSwipeActionClassName(
            ShowPreviewTextInListView,
            !!props.richContents?.RSVPUnit?.Component
        )
    );

    const { rowId } = props.mailListItemDataProps;
    const actionText = getSwipeActionText(swipeActionName, rowId);
    return (
        <div className={swipeActionStyles}>
            <span className={styles['mailListItem-swipeActionTitle']}>{actionText}</span>
        </div>
    );
};

function logSwipeAction(isLeft: boolean, swipeActionName: string) {
    logUsage('SwipeMailItem', [isLeft, swipeActionName], { isCore: true });
    signalWorkflowEvent(
        SWIPE_ACTION_BEGIN,
        { isUIAction: true, ...getMailActionCustomData() },
        { isCore: true }
    );
}

function longPressAction(props: MailListItemViewProps) {
    /**
     * Using remove first and then add because we are adding the same string always. So we want aria-live to pickup everytime it changes.
     * The utils dont change the store if the same string is present.
     */
    removeAriaLiveLabel(strings.selectedModeAriaLabel);
    addAriaLiveLabel(strings.selectedModeAriaLabel, 'assertive');
    const { rowId } = props.mailListItemDataProps;
    multiSelect(rowId, 'MailListItem' /* source */);
}

function isListViewMultiSelecteEnabled(): boolean {
    return getSelectedTableView().isInCheckedMode;
}

const getMailListItemCardSwipeActionClassName = (
    showPreviewText: boolean,
    showRSVPUnit: boolean
): SupportedItemCardSwipeActionClassNames => {
    if (showPreviewText && showRSVPUnit) {
        return 'mailListItemCard-swipeAction';
    }
    if (showPreviewText) {
        return 'mailListItemCard-withoutRsvp-swipeAction';
    }
    if (showRSVPUnit) {
        return 'mailListItemCard-withoutPreview-swipeAction';
    }
    return 'mailListItemCard-withoutPreview-withoutRsvp-swipeAction';
};
